import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Button,
    DatePicker,
    Descriptions,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Result,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
    message,
} from "antd";
import dayjs from "dayjs";
import {
    CopyOutlined,
    EditOutlined,
    EyeOutlined,
    InfoCircleOutlined,
    ReloadOutlined,
    SafetyCertificateOutlined,
    StopOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { get_all_clients } from "../../../Services/client.service";
import {
    createAdminWorkspaceAdjustment,
    getAdminCommercialPlan,
    getAdminWorkspaceReservations,
    getAdminWorkspaceSubscription,
    getAdminWorkspaceUsage,
    getAdminWorkspaceUsageEvents,
    listAdminCommercialPlans,
    listAdminPlanCapabilities,
    listAdminPolicyMessages,
    listAdminWorkspaceAdjustments,
    releaseAdminReservation,
    revokeAdminUsageAdjustment,
    updateAdminCommercialPlan,
    updateAdminPlanCapability,
    updateAdminPolicyMessage,
    upsertAdminWorkspaceSubscription,
} from "../../../Services/adminCommercialControl.service";
import {
    listAdminAuditEvents,
    listAdminStripeWebhookEvents,
} from "../../../Services/adminObservability.service";
import "./AdminCommercialControlPage.css";

const { Text, Title } = Typography;

const PLAN_STATUS_OPTIONS = ["active", "draft", "archived"];
const SUBSCRIPTION_STATUS_OPTIONS = ["active", "trialing", "suspended", "cancelled"];
const POLICY_SEVERITY_OPTIONS = ["info", "warning", "blocked"];
const CRITERIA_PROMPT_GENERATION_USAGE_TYPE = "criteria_prompt_generation";
const CUSTOM_CRITERIA_USAGE_TYPE = "custom_criteria";
const BONUS_CUSTOM_CRITERIA_ADJUSTMENT_TYPE = "bonus_custom_criteria";
const USAGE_TYPE_ORDER = [
    "complete_analysis",
    "supporting_document",
    "risk_comparison",
    CRITERIA_PROMPT_GENERATION_USAGE_TYPE,
    "rerun",
    CUSTOM_CRITERIA_USAGE_TYPE,
];
const DEFAULT_ADJUSTMENT_TYPE_OPTIONS = ["bonus_analyses", "quota_override", "incident_credit"];
const ADJUSTMENT_TYPE_OPTIONS_BY_USAGE = {
    [CUSTOM_CRITERIA_USAGE_TYPE]: [BONUS_CUSTOM_CRITERIA_ADJUSTMENT_TYPE],
};

const USAGE_TYPE_LABELS = {
    complete_analysis: "Analyses complètes",
    supporting_document: "Documents d'appui",
    risk_comparison: "Comparaisons risque",
    [CRITERIA_PROMPT_GENERATION_USAGE_TYPE]: "Génération prompts IA",
    rerun: "Relances",
    [CUSTOM_CRITERIA_USAGE_TYPE]: "Critères personnalisés",
};

const ADJUSTMENT_TYPE_LABELS = {
    bonus_analyses: "Bonus analyses",
    quota_override: "Override quota",
    incident_credit: "Crédit incident",
    bonus_workspace_users: "Bonus utilisateurs workspace",
    [BONUS_CUSTOM_CRITERIA_ADJUSTMENT_TYPE]: "Bonus critères personnalisés",
};

const SENSITIVE_METADATA_KEYS = new Set([
    "ai_comment",
    "ai_comments",
    "blocks",
    "clause_text",
    "comment",
    "comments",
    "completion",
    "content",
    "content_blocks",
    "contract_fingerprint_context",
    "contract_text",
    "detailed_view",
    "document_content",
    "document_text",
    "extracted_text",
    "file_content",
    "fingerprint",
    "fingerprint_context",
    "contract_profile_json",
    "full_text",
    "fused_blocks",
    "ia_comments",
    "llm_json",
    "matched_text",
    "memo",
    "messages",
    "output",
    "payload",
    "prompt",
    "quick_view",
    "quote",
    "quotes",
    "api_key",
    "authorization",
    "client_secret",
    "email_verification_token",
    "raw_text",
    "raw_payload_json",
    "response",
    "reset_token",
    "review_comments",
    "secret",
    "structured_text_json",
    "summary",
    "system_prompt",
    "textract_blocks",
    "text",
    "token",
    "user_prompt",
]);
const SENSITIVE_TEXT_PATTERN = /(api[_-]?key|authorization|bearer\s+|client[_-]?secret|password|reset[_-]?token|secret|sk_live|sk_test|token)/i;
const DEFAULT_OBSERVABILITY_LIMIT = 25;
const WEBHOOK_STATUS_OPTIONS = ["received", "processed", "failed"];

function unwrapData(response) {
    if (response && Object.prototype.hasOwnProperty.call(response, "data")) {
        return response.data;
    }
    return response;
}

function asArray(value) {
    return Array.isArray(value) ? value : [];
}

function formatJson(value) {
    return JSON.stringify(value ?? {}, null, 2);
}

function parseJsonField(value, fallback = {}) {
    if (value === undefined || value === null || String(value).trim() === "") {
        return fallback;
    }
    return JSON.parse(value);
}

const FIELD_LABELS = {
    adjustment_type: "Type d'ajustement",
    body_key: "Clé corps",
    cta_target: "Cible CTA",
    default_body_en: "Corps EN",
    default_body_fr: "Corps FR",
    default_title_en: "Titre EN",
    default_title_fr: "Titre FR",
    metadata_json: "Metadata JSON",
    name: "Nom",
    period_end: "Fin de période",
    period_start: "Début de période",
    plan_code: "Plan",
    plan_id: "Plan",
    quantity: "Quantité",
    reason: "Raison admin",
    severity: "Sévérité",
    status: "Statut",
    title_key: "Clé titre",
    usage_type: "Usage",
    value_json: "value_json",
};

function isFilled(value) {
    if (dayjs.isDayjs(value)) return value.isValid();
    if (typeof value === "string") return value.trim().length > 0;
    return value !== undefined && value !== null;
}

function requiredReady(values, fields) {
    return fields.every(field => isFilled(values?.[field]));
}

function validationFieldName(loc) {
    if (!Array.isArray(loc)) return null;
    const candidate = [...loc].reverse().find(part => typeof part === "string" && part !== "body");
    return candidate || null;
}

function friendlyValidationMessage(item) {
    const fieldName = validationFieldName(item?.loc);
    const label = FIELD_LABELS[fieldName] || fieldName || "Champ";
    const rawMessage = item?.msg || item?.message || "valeur invalide";
    const normalized = String(rawMessage).toLowerCase();

    if (normalized.includes("field required") || normalized.includes("missing")) {
        return `${label} obligatoire`;
    }
    if (normalized.includes("provide exactly one of plan_id or plan_code")) {
        return "Sélectionne exactement un plan actif.";
    }
    if (normalized.includes("period_end must be after period_start")) {
        return "La fin de période doit être après le début.";
    }
    if (normalized.includes("input should be a valid datetime")) {
        return `${label} doit être une date valide.`;
    }
    if (normalized.includes("json")) {
        return `${label} doit contenir du JSON valide.`;
    }

    return `${label}: ${rawMessage}`;
}

function getErrorMessage(error) {
    const responseData = error?.response?.data;
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
        const messages = detail.map(friendlyValidationMessage).filter(Boolean);
        return `Champs invalides : ${messages.join(" ; ")}`;
    }
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object") {
        return detail.msg || detail.message || JSON.stringify(detail);
    }
    if (responseData?.message) return responseData.message;
    if (responseData?.error) return responseData.error;
    if (error?.response?.status === 422) {
        return "Certains champs sont invalides. Vérifie les champs obligatoires et les formats.";
    }
    return error?.message || "Opération impossible pour le moment.";
}

function backendFieldErrors(error) {
    const detail = error?.response?.data?.detail;
    if (!Array.isArray(detail)) return [];

    return detail
        .map(item => {
            const fieldName = validationFieldName(item?.loc);
            if (!fieldName) return null;
            return {
                name: fieldName,
                errors: [friendlyValidationMessage(item)],
            };
        })
        .filter(Boolean);
}

function applyBackendFieldErrors(form, error) {
    const errors = backendFieldErrors(error);
    if (errors.length > 0) {
        form.setFields(errors);
    }
}

function notifyAdminError(messageApi, error) {
    messageApi.error({
        content: getErrorMessage(error),
        duration: 7,
    });
}

function getCurrentUser(state) {
    const slice = state.users || state.user || {};
    if (slice.currentUser && Object.keys(slice.currentUser).length) return slice.currentUser;
    if (slice.user && Object.keys(slice.user).length) return slice.user;
    return null;
}

function sanitizeMetadata(value, depth = 0) {
    if (depth > 5) return null;
    if (typeof value === "string") {
        return SENSITIVE_TEXT_PATTERN.test(value) ? "[REDACTED_FRONT_ADMIN]" : value;
    }
    if (value === null || value === undefined || typeof value !== "object") return value;
    if (Array.isArray(value)) return value.map(item => sanitizeMetadata(item, depth + 1));

    return Object.fromEntries(
        Object.entries(value)
            .filter(([key]) => !SENSITIVE_METADATA_KEYS.has(String(key).trim().toLowerCase()))
            .map(([key, child]) => [key, sanitizeMetadata(child, depth + 1)])
    );
}

function metadataPreview(value) {
    const sanitized = sanitizeMetadata(value ?? {});
    return JSON.stringify(sanitized ?? {}, null, 2);
}

function listPayload(response) {
    const data = unwrapData(response) || {};
    const items = asArray(data.items);
    return {
        items,
        limit: Number(data.limit) || DEFAULT_OBSERVABILITY_LIMIT,
        offset: Number(data.offset) || 0,
        total: Number(data.total) || items.length,
    };
}

function compactMetadata(fields) {
    return Object.fromEntries(
        Object.entries(fields).filter(([, value]) => value !== undefined && value !== null && value !== "")
    );
}

function limitValue(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return DEFAULT_OBSERVABILITY_LIMIT;
    return Math.min(Math.max(Math.trunc(numeric), 1), 100);
}

function statusTag(status) {
    const normalized = status || "unknown";
    const color =
        normalized === "active" || normalized === "confirmed" || normalized === "reserved"
            ? "green"
            : normalized === "archived" || normalized === "revoked" || normalized === "released"
                ? "default"
                : normalized === "blocked" || normalized === "failed"
                    ? "red"
                    : "blue";
    return <Tag color={color}>{normalized}</Tag>;
}

function formatDate(value) {
    if (!value) return "-";
    try {
        return new Intl.DateTimeFormat("fr-FR", {
            dateStyle: "short",
            timeStyle: "short",
        }).format(new Date(value));
    } catch (error) {
        return value;
    }
}

function defaultSubscriptionPeriod() {
    const start = dayjs().startOf("day");
    return {
        period_start: start,
        period_end: start.add(1, "month"),
    };
}

function toDatePickerValue(value, fallback = null) {
    if (!value) return fallback;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : fallback;
}

function toPeriodIso(value, boundary = "start") {
    const parsed = dayjs.isDayjs(value) ? value : dayjs(value);
    if (!parsed.isValid()) return value;
    return boundary === "end"
        ? parsed.endOf("day").toISOString()
        : parsed.startOf("day").toISOString();
}

function usageKeys(usageMap) {
    const keys = Object.keys(usageMap || {});
    return [
        ...USAGE_TYPE_ORDER.filter(key => keys.includes(key)),
        ...keys.filter(key => !USAGE_TYPE_ORDER.includes(key)).sort(),
    ];
}

function adjustmentTypeOptionsForUsage(usageType) {
    return ADJUSTMENT_TYPE_OPTIONS_BY_USAGE[usageType] || DEFAULT_ADJUSTMENT_TYPE_OPTIONS;
}

function clientOptionLabel(client) {
    const name = client?.name || "Client sans nom";
    const email = client?.contact_email ? ` - ${client.contact_email}` : "";
    return `${name}${email}`;
}

function remainingTone(value, unlimited) {
    if (unlimited) return "ok";
    if (value === null || value === undefined) return "neutral";
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return "neutral";
    if (numeric <= 0) return "danger";
    if (numeric <= 2) return "warning";
    return "ok";
}

function HelpTip({ title, items, placement = "rightTop" }) {
    const overlay = (
        <div className="admin-commercial-help-overlay">
            {title && <strong>{title}</strong>}
            {Array.isArray(items) && items.length > 0 && (
                <ol>
                    {items.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </ol>
            )}
        </div>
    );
    return (
        <Tooltip
            title={overlay}
            placement={placement}
            classNames={{ root: "admin-commercial-help-tooltip" }}
            styles={{ root: { maxWidth: 380 } }}
        >
            <button
                type="button"
                aria-label={`Aide : ${title || "écran"}`}
                className="admin-commercial-help-trigger"
            >
                <InfoCircleOutlined aria-hidden />
            </button>
        </Tooltip>
    );
}

function SectionTitle({ level = 3, title, hint, items, subtitle, action }) {
    return (
        <div className="admin-commercial-section-head">
            <div className="admin-commercial-section-head-text">
                <div className="admin-commercial-section-head-row">
                    <Title level={level} style={{ margin: 0 }}>{title}</Title>
                    {(hint || (items && items.length)) && (
                        <HelpTip title={hint} items={items} />
                    )}
                </div>
                {subtitle && <Text type="secondary">{subtitle}</Text>}
            </div>
            {action && <div className="admin-commercial-section-head-action">{action}</div>}
        </div>
    );
}

function WorkspaceLookup({
    clientId,
    onClientIdChange,
    onLoad,
    loading,
    clients,
    clientsLoading,
    buttonLabel = "Charger",
    helper,
}) {
    const hasClient = !!clientId?.trim();
    return (
        <div className="admin-commercial-lookup-card">
            <div className="admin-commercial-lookup-head">
                <UserOutlined aria-hidden />
                <div>
                    <div className="admin-commercial-lookup-title">Sélectionner un workspace</div>
                    <div className="admin-commercial-lookup-helper">
                        {helper || "Les données du workspace se chargent automatiquement dès la sélection."}
                    </div>
                </div>
            </div>
            <div className="admin-commercial-lookup">
                <Select
                    aria-label="Client / workspace"
                    value={clientId || undefined}
                    onChange={(value) => onClientIdChange(value || "")}
                    placeholder="Sélectionner un client / workspace"
                    loading={clientsLoading}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    options={asArray(clients).map(client => ({
                        value: client.id,
                        label: clientOptionLabel(client),
                    }))}
                    notFoundContent={clientsLoading ? "Chargement..." : "Aucun client disponible"}
                />
                <Tooltip title={hasClient ? "Forcer un rechargement" : "Sélectionne d'abord un workspace"}>
                    <Button
                        type="default"
                        icon={<ReloadOutlined />}
                        onClick={onLoad}
                        loading={loading}
                        disabled={!hasClient}
                        aria-label={buttonLabel}
                    >
                        Actualiser
                    </Button>
                </Tooltip>
            </div>
        </div>
    );
}

export default function AdminCommercialControlPage() {
    const currentUser = useSelector(getCurrentUser);
    const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";
    const [messageApi, contextHolder] = message.useMessage();

    const [plans, setPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("plans");
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);

    const [planDrawerOpen, setPlanDrawerOpen] = useState(false);
    const [planDetail, setPlanDetail] = useState(null);
    const [capabilities, setCapabilities] = useState([]);
    const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
    const [planModalOpen, setPlanModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [capabilityModalOpen, setCapabilityModalOpen] = useState(false);
    const [editingCapability, setEditingCapability] = useState(null);

    const [workspaceClientId, setWorkspaceClientId] = useState("");
    const [subscription, setSubscription] = useState(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);

    const [usageLoading, setUsageLoading] = useState(false);
    const [usageDiagnostic, setUsageDiagnostic] = useState(null);
    const [reservations, setReservations] = useState([]);
    const [usageEvents, setUsageEvents] = useState([]);
    const [releaseModalOpen, setReleaseModalOpen] = useState(false);
    const [releaseReservationRow, setReleaseReservationRow] = useState(null);

    const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);
    const [adjustments, setAdjustments] = useState([]);
    const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
    const [revokeAdjustmentModalOpen, setRevokeAdjustmentModalOpen] = useState(false);
    const [revokeAdjustmentRow, setRevokeAdjustmentRow] = useState(null);

    const [policyMessages, setPolicyMessages] = useState([]);
    const [policyLoading, setPolicyLoading] = useState(false);
    const [policyModalOpen, setPolicyModalOpen] = useState(false);
    const [editingPolicyMessage, setEditingPolicyMessage] = useState(null);
    const [auditFilters, setAuditFilters] = useState({
        event_type: "",
        request_id: "",
        client_id: "",
        limit: DEFAULT_OBSERVABILITY_LIMIT,
    });
    const [auditEvents, setAuditEvents] = useState([]);
    const [auditEventsTotal, setAuditEventsTotal] = useState(0);
    const [auditEventsLoading, setAuditEventsLoading] = useState(false);
    const [auditEventsError, setAuditEventsError] = useState(null);
    const [stripeWebhookFilters, setStripeWebhookFilters] = useState({
        stripe_event_type: "",
        processing_status: "",
        livemode: undefined,
        reconciliation_reason: "",
        limit: DEFAULT_OBSERVABILITY_LIMIT,
    });
    const [stripeWebhookEvents, setStripeWebhookEvents] = useState([]);
    const [stripeWebhookEventsTotal, setStripeWebhookEventsTotal] = useState(0);
    const [stripeWebhookEventsLoading, setStripeWebhookEventsLoading] = useState(false);
    const [stripeWebhookEventsError, setStripeWebhookEventsError] = useState(null);
    const [observabilityLoaded, setObservabilityLoaded] = useState(false);

    const [planForm] = Form.useForm();
    const [capabilityForm] = Form.useForm();
    const [subscriptionForm] = Form.useForm();
    const [releaseForm] = Form.useForm();
    const [adjustmentForm] = Form.useForm();
    const [revokeAdjustmentForm] = Form.useForm();
    const [policyForm] = Form.useForm();
    const planValues = Form.useWatch([], planForm) || {};
    const capabilityValues = Form.useWatch([], capabilityForm) || {};
    const subscriptionValues = Form.useWatch([], subscriptionForm) || {};
    const releaseValues = Form.useWatch([], releaseForm) || {};
    const adjustmentValues = Form.useWatch([], adjustmentForm) || {};
    const revokeAdjustmentValues = Form.useWatch([], revokeAdjustmentForm) || {};
    const policyValues = Form.useWatch([], policyForm) || {};

    const activePlansForSubscription = useMemo(
        () => plans.filter(plan => plan.status === "active"),
        [plans]
    );
    const canSubmitPlan = requiredReady(planValues, ["name", "status", "reason"]);
    const canSubmitCapability = requiredReady(capabilityValues, ["value_json", "reason"]);
    const canSubmitSubscription = Boolean(workspaceClientId.trim())
        && requiredReady(subscriptionValues, ["plan_code", "status", "period_start", "period_end", "reason"]);
    const canSubmitRelease = requiredReady(releaseValues, ["reason"]);
    const canCreateAdjustment = Boolean(workspaceClientId.trim());
    const canSubmitAdjustment = requiredReady(adjustmentValues, [
        "usage_type",
        "adjustment_type",
        "quantity",
        "period_start",
        "period_end",
        "reason",
    ]);
    const canSubmitRevokeAdjustment = requiredReady(revokeAdjustmentValues, ["reason"]);
    const canSubmitPolicy = requiredReady(policyValues, ["reason"]);

    const handleWorkspaceClientIdChange = useCallback((clientId) => {
        setWorkspaceClientId(clientId || "");
        setSubscription(null);
        setUsageDiagnostic(null);
        setReservations([]);
        setUsageEvents([]);
        setAdjustments([]);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get("tab");
        const clientId = params.get("client_id") || params.get("workspace_id");

        if (tab === "subscriptions" || tab === "usage" || tab === "adjustments" || tab === "observability") {
            setActiveTab(tab);
        }

        if (clientId) {
            setWorkspaceClientId(clientId);
        }
    }, []);

    // Auto-chargement : dès qu'un workspace est sélectionné (ou que l'admin
    // change d'onglet pour un onglet workspace-scoped), on déclenche le loader
    // correspondant si la donnée n'est pas déjà chargée / en cours de chargement.
    // Cela évite à l'admin de devoir cliquer manuellement sur "Charger ...".
    useEffect(() => {
        const id = workspaceClientId.trim();
        if (!id) return;
        if (activeTab === "subscriptions" && !subscription && !subscriptionLoading) {
            loadSubscription();
        } else if (activeTab === "usage" && !usageDiagnostic && !usageLoading) {
            loadUsageDiagnostics();
        } else if (activeTab === "adjustments" && adjustments.length === 0 && !adjustmentsLoading) {
            loadAdjustments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceClientId, activeTab]);

    const loadPlans = useCallback(async () => {
        setPlansLoading(true);
        try {
            const response = await listAdminCommercialPlans();
            setPlans(asArray(unwrapData(response)));
        } catch (error) {
            notifyAdminError(messageApi, error);
        } finally {
            setPlansLoading(false);
        }
    }, [messageApi]);

    const loadClients = useCallback(async () => {
        setClientsLoading(true);
        try {
            const response = await get_all_clients();
            setClients(asArray(unwrapData(response)));
        } catch (error) {
            setClients([]);
            const statusCode = error?.response?.status;
            if (statusCode !== 404) {
                notifyAdminError(messageApi, error);
            }
        } finally {
            setClientsLoading(false);
        }
    }, [messageApi]);

    const loadPolicyMessages = useCallback(async () => {
        setPolicyLoading(true);
        try {
            const response = await listAdminPolicyMessages();
            setPolicyMessages(asArray(unwrapData(response)));
        } catch (error) {
            notifyAdminError(messageApi, error);
        } finally {
            setPolicyLoading(false);
        }
    }, [messageApi]);

    const loadAuditEvents = useCallback(async ({ offset = 0, append = false } = {}) => {
        setAuditEventsLoading(true);
        setAuditEventsError(null);
        try {
            const response = await listAdminAuditEvents({
                event_type: auditFilters.event_type?.trim(),
                request_id: auditFilters.request_id?.trim(),
                client_id: auditFilters.client_id?.trim(),
                limit: limitValue(auditFilters.limit),
                offset,
            });
            const payload = listPayload(response);
            setAuditEvents(current => append ? [...current, ...payload.items] : payload.items);
            setAuditEventsTotal(payload.total);
        } catch (error) {
            setAuditEventsError(getErrorMessage(error));
            if (!append) {
                setAuditEvents([]);
                setAuditEventsTotal(0);
            }
        } finally {
            setAuditEventsLoading(false);
        }
    }, [auditFilters]);

    const loadStripeWebhookEvents = useCallback(async ({ offset = 0, append = false } = {}) => {
        setStripeWebhookEventsLoading(true);
        setStripeWebhookEventsError(null);
        try {
            const response = await listAdminStripeWebhookEvents({
                stripe_event_type: stripeWebhookFilters.stripe_event_type?.trim(),
                processing_status: stripeWebhookFilters.processing_status,
                livemode: stripeWebhookFilters.livemode,
                reconciliation_reason: stripeWebhookFilters.reconciliation_reason?.trim(),
                limit: limitValue(stripeWebhookFilters.limit),
                offset,
            });
            const payload = listPayload(response);
            setStripeWebhookEvents(current => append ? [...current, ...payload.items] : payload.items);
            setStripeWebhookEventsTotal(payload.total);
        } catch (error) {
            setStripeWebhookEventsError(getErrorMessage(error));
            if (!append) {
                setStripeWebhookEvents([]);
                setStripeWebhookEventsTotal(0);
            }
        } finally {
            setStripeWebhookEventsLoading(false);
        }
    }, [stripeWebhookFilters]);

    const loadObservabilityEvents = useCallback(() => {
        setObservabilityLoaded(true);
        loadAuditEvents();
        loadStripeWebhookEvents();
    }, [loadAuditEvents, loadStripeWebhookEvents]);

    useEffect(() => {
        if (activeTab === "observability" && !observabilityLoaded) {
            loadObservabilityEvents();
        }
    }, [activeTab, loadObservabilityEvents, observabilityLoaded]);

    useEffect(() => {
        if (isSuperAdmin) {
            loadPlans();
            loadClients();
        }
    }, [isSuperAdmin, loadClients, loadPlans]);

    if (!isSuperAdmin) {
        return (
            <Result
                status="403"
                title="Accès réservé SUPER_ADMIN"
                subTitle="Les droits produit et les droits d'administration restent séparés."
            />
        );
    }

    const loadPlanDetail = async (plan) => {
        setPlanDetail(plan);
        setPlanDrawerOpen(true);
        setCapabilitiesLoading(true);
        try {
            const [planResponse, capabilitiesResponse] = await Promise.all([
                getAdminCommercialPlan(plan.id),
                listAdminPlanCapabilities(plan.id),
            ]);
            setPlanDetail(unwrapData(planResponse));
            setCapabilities(asArray(unwrapData(capabilitiesResponse)));
        } catch (error) {
            notifyAdminError(messageApi, error);
        } finally {
            setCapabilitiesLoading(false);
        }
    };

    const openPlanEditor = (plan) => {
        setEditingPlan(plan);
        planForm.setFieldsValue({
            name: plan.name,
            status: plan.status,
            is_public: plan.is_public,
            sort_order: plan.sort_order,
            metadata_json: formatJson(plan.metadata_json),
            reason: "",
        });
        setPlanModalOpen(true);
    };

    const submitPlanPatch = async () => {
        const values = await planForm.validateFields();
        let metadataJson;
        try {
            metadataJson = parseJsonField(values.metadata_json, {});
        } catch (error) {
            planForm.setFields([{ name: "metadata_json", errors: ["JSON invalide"] }]);
            return;
        }

        try {
            await updateAdminCommercialPlan(editingPlan.id, {
                name: values.name,
                status: values.status,
                is_public: values.is_public,
                sort_order: values.sort_order,
                metadata_json: metadataJson,
                reason: values.reason,
            });
            messageApi.success("Plan mis à jour.");
            setPlanModalOpen(false);
            setEditingPlan(null);
            await loadPlans();
        } catch (error) {
            applyBackendFieldErrors(planForm, error);
            notifyAdminError(messageApi, error);
        }
    };

    const openCapabilityEditor = (capability) => {
        setEditingCapability(capability);
        capabilityForm.setFieldsValue({
            value_json: formatJson(capability.value_json),
            reason: "",
        });
        setCapabilityModalOpen(true);
    };

    const submitCapabilityPatch = async () => {
        const values = await capabilityForm.validateFields();
        let valueJson;
        try {
            valueJson = parseJsonField(values.value_json, null);
        } catch (error) {
            capabilityForm.setFields([{ name: "value_json", errors: ["JSON invalide"] }]);
            return;
        }

        try {
            await updateAdminPlanCapability(planDetail.id, editingCapability.capability_key, {
                value_json: valueJson,
                reason: values.reason,
            });
            messageApi.success("Capability mise à jour.");
            setCapabilityModalOpen(false);
            setEditingCapability(null);
            await loadPlanDetail(planDetail);
        } catch (error) {
            applyBackendFieldErrors(capabilityForm, error);
            notifyAdminError(messageApi, error);
        }
    };

    const loadSubscription = async () => {
        if (!workspaceClientId.trim()) return;
        setSubscriptionLoading(true);
        try {
            const response = await getAdminWorkspaceSubscription(workspaceClientId.trim());
            const data = unwrapData(response);
            const fallbackPeriod = defaultSubscriptionPeriod();
            setSubscription(data);
            subscriptionForm.setFieldsValue({
                plan_code: data?.plan_code || plans.find(plan => plan.id === data?.plan_id)?.code,
                status: data?.status || "active",
                period_start: toDatePickerValue(data?.period_start, fallbackPeriod.period_start),
                period_end: toDatePickerValue(data?.period_end, fallbackPeriod.period_end),
                included_analyses_override: data?.included_analyses_override,
                metadata_json: formatJson(data?.metadata_json),
                reason: "",
            });
        } catch (error) {
            const fallbackPeriod = defaultSubscriptionPeriod();
            setSubscription(null);
            subscriptionForm.setFieldsValue({
                plan_code: undefined,
                status: "active",
                period_start: fallbackPeriod.period_start,
                period_end: fallbackPeriod.period_end,
                included_analyses_override: undefined,
                metadata_json: "{}",
                reason: "",
            });
            if (error?.response?.status === 404) {
                messageApi.info("Aucune subscription existante pour ce workspace. Vous pouvez en créer une.");
            } else {
                notifyAdminError(messageApi, error);
            }
        } finally {
            setSubscriptionLoading(false);
        }
    };

    const submitSubscription = async () => {
        const values = await subscriptionForm.validateFields();
        let metadataJson;
        try {
            metadataJson = parseJsonField(values.metadata_json, {});
        } catch (error) {
            subscriptionForm.setFields([{ name: "metadata_json", errors: ["JSON invalide"] }]);
            return;
        }

        try {
            const payload = {
                plan_code: values.plan_code,
                status: values.status,
                period_start: toPeriodIso(values.period_start, "start"),
                period_end: toPeriodIso(values.period_end, "end"),
                included_analyses_override:
                    values.included_analyses_override === undefined || values.included_analyses_override === ""
                        ? null
                        : values.included_analyses_override,
                metadata_json: metadataJson,
                reason: values.reason,
            };
            const response = await upsertAdminWorkspaceSubscription(workspaceClientId.trim(), payload);
            setSubscription(unwrapData(response));
            messageApi.success("Subscription mise à jour.");
        } catch (error) {
            applyBackendFieldErrors(subscriptionForm, error);
            notifyAdminError(messageApi, error);
        }
    };

    const loadUsageDiagnostics = async () => {
        if (!workspaceClientId.trim()) return;
        setUsageLoading(true);
        try {
            const [usageResponse, reservationsResponse, eventsResponse] = await Promise.all([
                getAdminWorkspaceUsage(workspaceClientId.trim()),
                getAdminWorkspaceReservations(workspaceClientId.trim()),
                getAdminWorkspaceUsageEvents(workspaceClientId.trim()),
            ]);
            setUsageDiagnostic(unwrapData(usageResponse));
            setReservations(asArray(unwrapData(reservationsResponse)));
            setUsageEvents(asArray(unwrapData(eventsResponse)));
        } catch (error) {
            notifyAdminError(messageApi, error);
        } finally {
            setUsageLoading(false);
        }
    };

    const openReleaseModal = (reservation) => {
        setReleaseReservationRow(reservation);
        releaseForm.setFieldsValue({ reason: "", metadata_json: "{}" });
        setReleaseModalOpen(true);
    };

    const submitReleaseReservation = async () => {
        const values = await releaseForm.validateFields();
        let metadataJson;
        try {
            metadataJson = parseJsonField(values.metadata_json, {});
        } catch (error) {
            releaseForm.setFields([{ name: "metadata_json", errors: ["JSON invalide"] }]);
            return;
        }

        try {
            await releaseAdminReservation(releaseReservationRow.id, {
                reason: values.reason,
                metadata_json: metadataJson,
            });
            messageApi.success("Réservation libérée.");
            setReleaseModalOpen(false);
            setReleaseReservationRow(null);
            await loadUsageDiagnostics();
        } catch (error) {
            applyBackendFieldErrors(releaseForm, error);
            notifyAdminError(messageApi, error);
        }
    };

    const loadAdjustments = async () => {
        if (!workspaceClientId.trim()) return;
        setAdjustmentsLoading(true);
        try {
            const response = await listAdminWorkspaceAdjustments(workspaceClientId.trim());
            setAdjustments(asArray(unwrapData(response)));
        } catch (error) {
            notifyAdminError(messageApi, error);
        } finally {
            setAdjustmentsLoading(false);
        }
    };

    const submitAdjustment = async () => {
        const values = await adjustmentForm.validateFields();
        let metadataJson;
        try {
            metadataJson = parseJsonField(values.metadata_json, {});
        } catch (error) {
            adjustmentForm.setFields([{ name: "metadata_json", errors: ["JSON invalide"] }]);
            return;
        }

        const toIso = (value) => {
            if (!value) return value;
            if (dayjs.isDayjs(value)) return value.toISOString();
            return value;
        };

        try {
            await createAdminWorkspaceAdjustment(workspaceClientId.trim(), {
                usage_type: values.usage_type,
                adjustment_type: values.adjustment_type,
                quantity: values.quantity ?? null,
                period_start: toIso(values.period_start),
                period_end: toIso(values.period_end),
                metadata_json: metadataJson,
                reason: values.reason,
            });
            messageApi.success("Adjustment créé.");
            setAdjustmentModalOpen(false);
            adjustmentForm.resetFields();
            await loadAdjustments();
        } catch (error) {
            applyBackendFieldErrors(adjustmentForm, error);
            notifyAdminError(messageApi, error);
        }
    };

    const openRevokeAdjustmentModal = (adjustment) => {
        setRevokeAdjustmentRow(adjustment);
        revokeAdjustmentForm.setFieldsValue({ reason: "", metadata_json: "{}" });
        setRevokeAdjustmentModalOpen(true);
    };

    const submitRevokeAdjustment = async () => {
        const values = await revokeAdjustmentForm.validateFields();
        let metadataJson;
        try {
            metadataJson = parseJsonField(values.metadata_json, {});
        } catch (error) {
            revokeAdjustmentForm.setFields([{ name: "metadata_json", errors: ["JSON invalide"] }]);
            return;
        }

        try {
            await revokeAdminUsageAdjustment(revokeAdjustmentRow.id, {
                reason: values.reason,
                metadata_json: metadataJson,
            });
            messageApi.success("Adjustment révoqué.");
            setRevokeAdjustmentModalOpen(false);
            setRevokeAdjustmentRow(null);
            await loadAdjustments();
        } catch (error) {
            applyBackendFieldErrors(revokeAdjustmentForm, error);
            notifyAdminError(messageApi, error);
        }
    };

    const openPolicyEditor = (policyMessage) => {
        setEditingPolicyMessage(policyMessage);
        policyForm.setFieldsValue({
            title_key: policyMessage.title_key,
            body_key: policyMessage.body_key,
            default_title_fr: policyMessage.default_title_fr,
            default_body_fr: policyMessage.default_body_fr,
            default_title_en: policyMessage.default_title_en,
            default_body_en: policyMessage.default_body_en,
            cta_label_key: policyMessage.cta_label_key,
            default_cta_label_fr: policyMessage.default_cta_label_fr,
            default_cta_label_en: policyMessage.default_cta_label_en,
            cta_target: policyMessage.cta_target,
            severity: policyMessage.severity,
            metadata_json: formatJson(policyMessage.metadata_json),
            reason: "",
        });
        setPolicyModalOpen(true);
    };

    const submitPolicyPatch = async () => {
        const values = await policyForm.validateFields();
        let metadataJson;
        try {
            metadataJson = parseJsonField(values.metadata_json, {});
        } catch (error) {
            policyForm.setFields([{ name: "metadata_json", errors: ["JSON invalide"] }]);
            return;
        }

        try {
            await updateAdminPolicyMessage(editingPolicyMessage.code, {
                ...values,
                metadata_json: metadataJson,
            });
            messageApi.success("Message policy mis à jour.");
            setPolicyModalOpen(false);
            setEditingPolicyMessage(null);
            await loadPolicyMessages();
        } catch (error) {
            applyBackendFieldErrors(policyForm, error);
            notifyAdminError(messageApi, error);
        }
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        if (key === "policy_messages" && policyMessages.length === 0) {
            loadPolicyMessages();
        }
        if (key === "observability" && !observabilityLoaded) {
            loadObservabilityEvents();
        }
    };

    const copyValue = async (value, label) => {
        if (!value || !navigator?.clipboard?.writeText) return;
        try {
            await navigator.clipboard.writeText(String(value));
            messageApi.success(`${label} copié.`);
        } catch (error) {
            messageApi.warning("Copie impossible dans ce navigateur.");
        }
    };

    const renderCopyCell = (value, label) => {
        if (!value) return "-";
        return (
            <Space size={4} className="admin-commercial-copy-cell">
                <Text code ellipsis className="admin-commercial-id-text">{value}</Text>
                <Tooltip title={`Copier ${label}`}>
                    <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        aria-label={`Copier ${label} ${value}`}
                        onClick={() => copyValue(value, label)}
                    />
                </Tooltip>
            </Space>
        );
    };

    const planColumns = [
        { title: "Code", dataIndex: "code", width: 130, fixed: "left" },
        { title: "Version", dataIndex: "version", width: 90 },
        { title: "Nom", dataIndex: "name", width: 220 },
        { title: "Statut", dataIndex: "status", width: 110, render: statusTag },
        { title: "Courant", dataIndex: "is_current", width: 100, render: value => value ? <Tag color="green">oui</Tag> : <Tag>non</Tag> },
        { title: "Public", dataIndex: "is_public", width: 100, render: value => value ? <Tag color="blue">oui</Tag> : <Tag>non</Tag> },
        { title: "Quota scope", dataIndex: "quota_scope", width: 140 },
        { title: "Ordre", dataIndex: "sort_order", width: 90 },
        {
            title: "Actions",
            key: "actions",
            width: 160,
            fixed: "right",
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title="Voir capabilities">
                        <Button
                            aria-label={`Voir capabilities ${record.code}`}
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => loadPlanDetail(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Modifier les champs admin autorisés">
                        <Button
                            aria-label={`Modifier plan ${record.code}`}
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => openPlanEditor(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const capabilityColumns = [
        { title: "Capability", dataIndex: "capability_key", width: 260 },
        {
            title: "value_json",
            dataIndex: "value_json",
            render: value => <pre className="admin-commercial-json-preview">{formatJson(value)}</pre>,
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => (
                <Button
                    aria-label={`Modifier capability ${record.capability_key}`}
                    icon={<EditOutlined />}
                    onClick={() => openCapabilityEditor(record)}
                >
                    Editer
                </Button>
            ),
        },
    ];

    const usageMap = usageDiagnostic?.usage || {};
    const customCriteriaUsage = usageDiagnostic?.custom_criteria;
    const usageRows = [
        ...usageKeys(usageMap).map(key => ({ usage_type: key, ...usageMap[key] })),
        ...(customCriteriaUsage && !usageMap[CUSTOM_CRITERIA_USAGE_TYPE]
            ? [{ usage_type: CUSTOM_CRITERIA_USAGE_TYPE, reserved: null, ...customCriteriaUsage }]
            : []),
    ];
    const showReservationActionsColumn = reservations.some(record => record.status === "reserved");
    const showAdjustmentActionsColumn = adjustments.some(record => record.status === "active");

    const reservationsColumns = [
        { title: "ID", dataIndex: "id", width: 220, fixed: "left" },
        { title: "Usage", dataIndex: "usage_type", width: 160, render: value => USAGE_TYPE_LABELS[value] || value },
        { title: "Statut", dataIndex: "status", width: 120, render: statusTag },
        { title: "Quantité", dataIndex: "quantity", width: 90 },
        { title: "Analysis", dataIndex: "analysis_id", width: 210, render: value => value || "-" },
        { title: "Expiration", dataIndex: "expires_at", width: 170, render: formatDate },
        {
            title: "Metadata",
            dataIndex: "metadata_json",
            render: value => <pre className="admin-commercial-json-preview">{metadataPreview(value)}</pre>,
        },
        {
            title: "Actions",
            key: "actions",
            width: 130,
            fixed: "right",
            render: (_, record) => (
                record.status === "reserved" ? (
                    <Button
                        danger
                        aria-label={`Libérer reservation ${record.id}`}
                        icon={<StopOutlined />}
                        onClick={() => openReleaseModal(record)}
                    >
                        Libérer
                    </Button>
                ) : (
                    <Text type="secondary">Lecture seule</Text>
                )
            ),
        },
    ].filter(column => column.key !== "actions" || showReservationActionsColumn);

    const usageEventsColumns = [
        { title: "Usage", dataIndex: "usage_type", width: 160, render: value => USAGE_TYPE_LABELS[value] || value },
        { title: "Event", dataIndex: "event_type", width: 120, render: statusTag },
        { title: "Quantité", dataIndex: "quantity", width: 90 },
        { title: "Code", dataIndex: "code", width: 170, render: value => value || "-" },
        { title: "Créé", dataIndex: "created_at", width: 170, render: formatDate },
        {
            title: "Metadata",
            dataIndex: "metadata_json",
            render: value => <pre className="admin-commercial-json-preview">{metadataPreview(value)}</pre>,
        },
    ];

    const adjustmentColumns = [
        { title: "Usage", dataIndex: "usage_type", width: 170, render: value => USAGE_TYPE_LABELS[value] || value },
        { title: "Type", dataIndex: "adjustment_type", width: 160 },
        { title: "Statut", dataIndex: "status", width: 110, render: statusTag },
        { title: "Quantité", dataIndex: "quantity", width: 90, render: value => value ?? "-" },
        { title: "Début", dataIndex: "period_start", width: 170, render: formatDate },
        { title: "Fin", dataIndex: "period_end", width: 170, render: formatDate },
        { title: "Raison", dataIndex: "reason", width: 260, render: value => value || "-" },
        {
            title: "Actions",
            key: "actions",
            width: 130,
            fixed: "right",
            render: (_, record) => (
                record.status === "active" ? (
                    <Button
                        danger
                        aria-label={`Révoquer adjustment ${record.id}`}
                        onClick={() => openRevokeAdjustmentModal(record)}
                    >
                        Révoquer
                    </Button>
                ) : (
                    <Text type="secondary">Lecture seule</Text>
                )
            ),
        },
    ].filter(column => column.key !== "actions" || showAdjustmentActionsColumn);

    const policyColumns = [
        { title: "Code", dataIndex: "code", width: 210, fixed: "left" },
        { title: "Sévérité", dataIndex: "severity", width: 110, render: statusTag },
        { title: "Titre FR", dataIndex: "default_title_fr", width: 240 },
        { title: "Corps FR", dataIndex: "default_body_fr", ellipsis: true },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            fixed: "right",
            render: (_, record) => (
                <Button
                    aria-label={`Modifier policy ${record.code}`}
                    icon={<EditOutlined />}
                    onClick={() => openPolicyEditor(record)}
                >
                    Editer
                </Button>
            ),
        },
    ];

    const auditEventColumns = [
        { title: "Date", dataIndex: "occurred_at", width: 170, fixed: "left", render: formatDate },
        { title: "Event type", dataIndex: "event_type", width: 230 },
        { title: "Outcome", dataIndex: "outcome", width: 180, render: value => value || "-" },
        { title: "Client", dataIndex: "client_id", width: 220, render: value => renderCopyCell(value, "client_id") },
        { title: "Actor user", dataIndex: "actor_user_id", width: 220, render: value => renderCopyCell(value, "actor_user_id") },
        { title: "Request", dataIndex: "request_id", width: 240, render: value => renderCopyCell(value, "request_id") },
        {
            title: "Metadata redacted",
            dataIndex: "metadata_json",
            render: value => <pre className="admin-commercial-json-preview">{metadataPreview(value)}</pre>,
        },
    ];

    const stripeWebhookColumns = [
        { title: "Date", dataIndex: "created_at", width: 170, fixed: "left", render: formatDate },
        { title: "Stripe event", dataIndex: "stripe_event_id", width: 240, render: value => renderCopyCell(value, "stripe_event_id") },
        { title: "Event type", dataIndex: "stripe_event_type", width: 220 },
        { title: "Status", dataIndex: "processing_status", width: 140, render: statusTag },
        {
            title: "Mode",
            dataIndex: "livemode",
            width: 100,
            render: value => value === true ? <Tag color="green">live</Tag> : value === false ? <Tag>test</Tag> : "-",
        },
        { title: "Object", dataIndex: "stripe_object_id", width: 220, render: value => renderCopyCell(value, "object_id") },
        { title: "Customer", dataIndex: "stripe_customer_id", width: 190, render: value => renderCopyCell(value, "customer_id") },
        { title: "Subscription", dataIndex: "stripe_subscription_id", width: 210, render: value => renderCopyCell(value, "subscription_id") },
        { title: "Checkout", dataIndex: "stripe_checkout_session_id", width: 220, render: value => renderCopyCell(value, "checkout_session_id") },
        { title: "Invoice", dataIndex: "stripe_invoice_id", width: 190, render: value => renderCopyCell(value, "invoice_id") },
        {
            title: "Reconciliation",
            key: "reconciliation",
            width: 260,
            render: (_, record) => record.reconciliation_reason || record.diagnostic_message || "-",
        },
        {
            title: "Metadata allowlist",
            key: "metadata_allowlist",
            width: 260,
            render: (_, record) => (
                <pre className="admin-commercial-json-preview">
                    {metadataPreview(compactMetadata({
                        client_id: record.metadata_client_id,
                        user_id: record.metadata_user_id,
                        workspace_id: record.metadata_workspace_id,
                        plan_code: record.metadata_plan_code,
                    }))}
                </pre>
            ),
        },
    ];

    const renderPlansTab = () => (
        <div className="admin-commercial-section">
            <SectionTitle
                title="Plans commerciaux"
                subtitle="Les plans et capabilities sont lus depuis le backend. Codes, versions et identifiants ne sont pas éditables ici."
                hint="Catalogue des plans"
                items={[
                    "Cliquer sur l'œil pour voir les capabilities d'un plan.",
                    "Cliquer sur le crayon pour modifier un plan (champs admin uniquement).",
                    "Le bouton Rafraîchir recharge la liste depuis le backend.",
                ]}
                action={(
                    <Button icon={<ReloadOutlined />} onClick={loadPlans} loading={plansLoading}>
                        Rafraîchir
                    </Button>
                )}
            />
            <Table
                columns={planColumns}
                dataSource={plans}
                loading={plansLoading}
                rowKey="id"
                size="middle"
                tableLayout="fixed"
                scroll={{ x: 1180 }}
                pagination={{ pageSize: 8, showSizeChanger: false }}
            />
        </div>
    );

    const renderSubscriptionTab = () => (
        <div className="admin-commercial-section">
            <SectionTitle
                title="Workspace / subscription"
                subtitle="Attribuer ou ajuster le plan d'un client (droits produit uniquement)."
                hint="Attribuer une licence"
                items={[
                    "Sélectionne le client / workspace puis charge sa subscription.",
                    "Choisis le plan, le statut et la période, puis renseigne une raison admin.",
                    "Aucun calcul de prorata, remboursement ou facture n'est déclenché.",
                ]}
            />
            <Alert
                type="info"
                showIcon
                className="admin-commercial-alert"
                message="Ce changement modifie les droits produit uniquement. Aucun prorata financier, remboursement, crédit ou facture n’est calculé automatiquement dans Verdictia V1."
            />
            <WorkspaceLookup
                clientId={workspaceClientId}
                onClientIdChange={handleWorkspaceClientIdChange}
                onLoad={loadSubscription}
                loading={subscriptionLoading}
                clients={clients}
                clientsLoading={clientsLoading}
                buttonLabel="Charger subscription"
                helper="La subscription se charge automatiquement dès la sélection du workspace. Utilise Actualiser pour forcer un rechargement."
            />
            {subscription && (
                <Descriptions bordered size="small" className="admin-commercial-descriptions" column={2}>
                    <Descriptions.Item label="Plan actuel">{subscription.plan_code || subscription.plan_id}</Descriptions.Item>
                    <Descriptions.Item label="Statut">{statusTag(subscription.status)}</Descriptions.Item>
                    <Descriptions.Item label="Début">{formatDate(subscription.period_start)}</Descriptions.Item>
                    <Descriptions.Item label="Fin">{formatDate(subscription.period_end)}</Descriptions.Item>
                </Descriptions>
            )}
            <Form
                form={subscriptionForm}
                layout="vertical"
                className="admin-commercial-form-grid"
                initialValues={{ status: "active", metadata_json: "{}", ...defaultSubscriptionPeriod() }}
            >
                <Form.Item name="plan_code" label="Plan backend" rules={[{ required: true, message: "Plan requis" }]}>
                    <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="Sélectionner un plan actif"
                        options={activePlansForSubscription.map(plan => ({
                            value: plan.code,
                            label: `${plan.code} v${plan.version} - ${plan.name}`,
                        }))}
                    />
                </Form.Item>
                <Form.Item name="status" label="Statut" rules={[{ required: true, message: "Statut requis" }]}>
                    <Select options={SUBSCRIPTION_STATUS_OPTIONS.map(value => ({ value, label: value }))} />
                </Form.Item>
                <Form.Item name="period_start" label="Début de période" rules={[{ required: true, message: "Début requis" }]}>
                    <DatePicker
                        format="YYYY-MM-DD"
                        placeholder="Début"
                        style={{ width: "100%" }}
                    />
                </Form.Item>
                <Form.Item name="period_end" label="Fin de période" rules={[{ required: true, message: "Fin requise" }]}>
                    <DatePicker
                        format="YYYY-MM-DD"
                        placeholder="Fin"
                        style={{ width: "100%" }}
                    />
                </Form.Item>
                <Form.Item name="included_analyses_override" label="included_analyses_override">
                    <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                    name="reason"
                    label="Raison admin"
                    rules={[{ required: true, message: "Raison obligatoire" }]}
                >
                    <Input placeholder="Ex. correction support validée" />
                </Form.Item>
                <Form.Item name="metadata_json" label="metadata_json" className="admin-commercial-form-wide">
                    <Input.TextArea rows={5} spellCheck={false} />
                </Form.Item>
                <Form.Item className="admin-commercial-form-wide">
                    <Button type="primary" onClick={submitSubscription} disabled={!canSubmitSubscription}>
                        Enregistrer subscription
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );

    const renderUsageDiagnosticsTab = () => (
        <div className="admin-commercial-section">
            <SectionTitle
                title="Usage diagnostics"
                subtitle="Vue commerciale et technique sanitisée. Aucun texte contractuel, commentaire IA ou profil du contrat complet n'est affiché."
                hint="Diagnostiquer un workspace"
                items={[
                    "Sélectionne un workspace puis clique sur Charger diagnostics.",
                    "Les vignettes en haut résument les quotas restants (vert / orange / rouge).",
                    "Onglet Reservations : libérer une réservation au statut reserved si nécessaire.",
                ]}
            />
            <WorkspaceLookup
                clientId={workspaceClientId}
                onClientIdChange={handleWorkspaceClientIdChange}
                onLoad={loadUsageDiagnostics}
                loading={usageLoading}
                clients={clients}
                clientsLoading={clientsLoading}
                buttonLabel="Charger diagnostics"
                helper="Quotas, réservations et événements d'usage se chargent automatiquement à la sélection du workspace."
            />
            <div className="admin-commercial-usage-grid">
                {usageRows.length === 0 && <Alert type="info" showIcon message="Aucun usage chargé." />}
                {usageRows.map(row => {
                    const tone = remainingTone(row.remaining, row.unlimited);
                    return (
                        <div className={`admin-commercial-usage-tile tone-${tone}`} key={row.usage_type}>
                            <div className="admin-commercial-usage-tile-head">
                                <Text strong>{USAGE_TYPE_LABELS[row.usage_type] || row.usage_type}</Text>
                                <span className={`admin-commercial-usage-pill tone-${tone}`}>
                                    {row.unlimited ? "illimité" : `restant ${row.remaining ?? "-"}`}
                                </span>
                            </div>
                            <div className="admin-commercial-usage-metrics">
                                <span>utilisé <strong>{row.used ?? "-"}</strong></span>
                                <span>réservé <strong>{row.reserved ?? "-"}</strong></span>
                                <span>ajustements <strong>{row.adjustments ?? "-"}</strong></span>
                                <span>restant <strong>{row.unlimited ? "unlimited" : row.remaining ?? "-"}</strong></span>
                            </div>
                        </div>
                    );
                })}
            </div>
            <Tabs
                className="admin-commercial-nested-tabs"
                items={[
                    {
                        key: "reservations",
                        label: "Reservations",
                        children: (
                            <Table
                                columns={reservationsColumns}
                                dataSource={reservations}
                                loading={usageLoading}
                                rowKey="id"
                                size="small"
                                tableLayout="fixed"
                                scroll={{ x: 1250 }}
                                pagination={{ pageSize: 6, showSizeChanger: false }}
                            />
                        ),
                    },
                    {
                        key: "usage_events",
                        label: "Usage events",
                        children: (
                            <Table
                                columns={usageEventsColumns}
                                dataSource={usageEvents}
                                loading={usageLoading}
                                rowKey="id"
                                size="small"
                                tableLayout="fixed"
                                scroll={{ x: 1050 }}
                                pagination={{ pageSize: 6, showSizeChanger: false }}
                            />
                        ),
                    },
                ]}
            />
        </div>
    );

    const renderAdjustmentsTab = () => (
        <div className="admin-commercial-section">
            <SectionTitle
                title="Adjustments"
                subtitle="Bonus produit attribués manuellement à un workspace pour une période donnée."
                hint="Créer un bonus produit"
                items={[
                    "Charge le workspace concerné pour voir l'historique de ses adjustments.",
                    "Clique sur Créer adjustment, choisis l'usage, le type et la période.",
                    "Renseigne une raison admin claire (validée par un manager).",
                ]}
                action={(
                    <Button
                        type="primary"
                        onClick={() => {
                            adjustmentForm.setFieldsValue({ metadata_json: "{}" });
                            setAdjustmentModalOpen(true);
                        }}
                        disabled={!canCreateAdjustment}
                    >
                        Créer adjustment
                    </Button>
                )}
            />
            <Alert
                type="info"
                showIcon
                className="admin-commercial-alert"
                message="Les adjustments sont des bonus produit, pas des remboursements financiers."
            />
            <WorkspaceLookup
                clientId={workspaceClientId}
                onClientIdChange={handleWorkspaceClientIdChange}
                onLoad={loadAdjustments}
                loading={adjustmentsLoading}
                clients={clients}
                clientsLoading={clientsLoading}
                buttonLabel="Charger adjustments"
                helper="Les bonus produit se chargent automatiquement dès la sélection du workspace."
            />
            <Table
                columns={adjustmentColumns}
                dataSource={adjustments}
                loading={adjustmentsLoading}
                rowKey="id"
                size="middle"
                tableLayout="fixed"
                scroll={{ x: 1280 }}
                pagination={{ pageSize: 8, showSizeChanger: false }}
            />
        </div>
    );

    const renderPolicyMessagesTab = () => (
        <div className="admin-commercial-section">
            <SectionTitle
                title="Policy messages"
                subtitle="Édition des messages existants uniquement. Aucun code policy n'est créé ou supprimé en V1."
                hint="Éditer un message policy"
                items={[
                    "Sélectionne le message dans la liste puis clique sur Éditer.",
                    "Modifie titre / corps FR et EN, sévérité, CTA cible.",
                    "Renseigne une raison admin avant d'enregistrer.",
                ]}
                action={(
                    <Button icon={<ReloadOutlined />} onClick={loadPolicyMessages} loading={policyLoading}>
                        Rafraîchir
                    </Button>
                )}
            />
            <Table
                columns={policyColumns}
                dataSource={policyMessages}
                loading={policyLoading}
                rowKey="id"
                size="middle"
                tableLayout="fixed"
                scroll={{ x: 1040 }}
                pagination={{ pageSize: 8, showSizeChanger: false }}
            />
        </div>
    );

    const renderAuditEventsPanel = () => (
        <div className="admin-commercial-observability-pane">
            <SectionTitle
                title="Audit events"
                subtitle="Événements persistés côté backend, affichés en lecture seule avec metadata redacted."
                action={(
                    <Button icon={<ReloadOutlined />} onClick={() => loadAuditEvents()} loading={auditEventsLoading}>
                        Charger audit events
                    </Button>
                )}
            />
            <div className="admin-commercial-filter-grid">
                <Input
                    aria-label="Filtre audit event_type"
                    placeholder="event_type"
                    value={auditFilters.event_type}
                    onChange={event => setAuditFilters(current => ({ ...current, event_type: event.target.value }))}
                />
                <Input
                    aria-label="Filtre audit request_id"
                    placeholder="request_id"
                    value={auditFilters.request_id}
                    onChange={event => setAuditFilters(current => ({ ...current, request_id: event.target.value }))}
                />
                <Input
                    aria-label="Filtre audit client_id"
                    placeholder="client_id"
                    value={auditFilters.client_id}
                    onChange={event => setAuditFilters(current => ({ ...current, client_id: event.target.value }))}
                />
                <InputNumber
                    aria-label="Limite audit events"
                    min={1}
                    max={100}
                    value={auditFilters.limit}
                    onChange={value => setAuditFilters(current => ({ ...current, limit: value }))}
                    style={{ width: "100%" }}
                />
                <Button onClick={() => loadAuditEvents()} loading={auditEventsLoading}>
                    Appliquer
                </Button>
            </div>
            {auditEventsError && (
                <Alert
                    type={auditEventsError.toLowerCase().includes("super") || auditEventsError.includes("403") ? "warning" : "error"}
                    showIcon
                    message={auditEventsError}
                />
            )}
            <Table
                columns={auditEventColumns}
                dataSource={auditEvents}
                loading={auditEventsLoading}
                rowKey={record => record.id || `${record.event_type}-${record.request_id}-${record.occurred_at}`}
                size="small"
                tableLayout="fixed"
                scroll={{ x: 1460 }}
                pagination={false}
                locale={{ emptyText: auditEventsError ? "Chargement impossible." : "Aucun audit event." }}
            />
            {auditEvents.length < auditEventsTotal && (
                <div className="admin-commercial-load-more">
                    <Button
                        onClick={() => loadAuditEvents({ offset: auditEvents.length, append: true })}
                        loading={auditEventsLoading}
                    >
                        Charger plus
                    </Button>
                </div>
            )}
        </div>
    );

    const renderStripeWebhooksPanel = () => (
        <div className="admin-commercial-observability-pane">
            <SectionTitle
                title="Stripe webhooks"
                subtitle="Vue support redacted des événements Stripe persistés. Aucun payload brut Stripe n'est affiché."
                action={(
                    <Button icon={<ReloadOutlined />} onClick={() => loadStripeWebhookEvents()} loading={stripeWebhookEventsLoading}>
                        Charger Stripe webhooks
                    </Button>
                )}
            />
            <div className="admin-commercial-filter-grid">
                <Input
                    aria-label="Filtre Stripe event type"
                    placeholder="stripe_event_type"
                    value={stripeWebhookFilters.stripe_event_type}
                    onChange={event => setStripeWebhookFilters(current => ({ ...current, stripe_event_type: event.target.value }))}
                />
                <Select
                    aria-label="Filtre Stripe status"
                    allowClear
                    placeholder="processing_status"
                    value={stripeWebhookFilters.processing_status || undefined}
                    options={WEBHOOK_STATUS_OPTIONS.map(value => ({ value, label: value }))}
                    onChange={value => setStripeWebhookFilters(current => ({ ...current, processing_status: value || "" }))}
                />
                <Select
                    aria-label="Filtre Stripe livemode"
                    allowClear
                    placeholder="livemode"
                    value={stripeWebhookFilters.livemode}
                    options={[
                        { value: true, label: "live" },
                        { value: false, label: "test" },
                    ]}
                    onChange={value => setStripeWebhookFilters(current => ({ ...current, livemode: value }))}
                />
                <Input
                    aria-label="Filtre Stripe reconciliation"
                    placeholder="reconciliation_reason"
                    value={stripeWebhookFilters.reconciliation_reason}
                    onChange={event => setStripeWebhookFilters(current => ({ ...current, reconciliation_reason: event.target.value }))}
                />
                <InputNumber
                    aria-label="Limite Stripe webhooks"
                    min={1}
                    max={100}
                    value={stripeWebhookFilters.limit}
                    onChange={value => setStripeWebhookFilters(current => ({ ...current, limit: value }))}
                    style={{ width: "100%" }}
                />
                <Button onClick={() => loadStripeWebhookEvents()} loading={stripeWebhookEventsLoading}>
                    Appliquer
                </Button>
            </div>
            {stripeWebhookEventsError && (
                <Alert
                    type={stripeWebhookEventsError.toLowerCase().includes("super") || stripeWebhookEventsError.includes("403") ? "warning" : "error"}
                    showIcon
                    message={stripeWebhookEventsError}
                />
            )}
            <Table
                columns={stripeWebhookColumns}
                dataSource={stripeWebhookEvents}
                loading={stripeWebhookEventsLoading}
                rowKey={record => record.id || record.stripe_event_id}
                size="small"
                tableLayout="fixed"
                scroll={{ x: 2450 }}
                pagination={false}
                locale={{ emptyText: stripeWebhookEventsError ? "Chargement impossible." : "Aucun webhook Stripe." }}
            />
            {stripeWebhookEvents.length < stripeWebhookEventsTotal && (
                <div className="admin-commercial-load-more">
                    <Button
                        onClick={() => loadStripeWebhookEvents({ offset: stripeWebhookEvents.length, append: true })}
                        loading={stripeWebhookEventsLoading}
                    >
                        Charger plus
                    </Button>
                </div>
            )}
        </div>
    );

    const renderObservabilityTab = () => (
        <div className="admin-commercial-section">
            <SectionTitle
                title="Observability support"
                subtitle="Consultation read-only des événements déjà persistés par le backend B1."
                hint="Diagnostiquer sans payload brut"
                items={[
                    "Audit events : filtrer par type, request_id ou client_id.",
                    "Stripe webhooks : filtrer par type, statut, livemode ou reconciliation reason.",
                    "Les metadata sont sanitisées côté front et les payloads Stripe bruts ne sont pas rendus.",
                ]}
                action={(
                    <Button icon={<ReloadOutlined />} onClick={loadObservabilityEvents} loading={auditEventsLoading || stripeWebhookEventsLoading}>
                        Rafraîchir observability
                    </Button>
                )}
            />
            <Tabs
                className="admin-commercial-nested-tabs"
                items={[
                    { key: "audit_events", label: "Audit events", children: renderAuditEventsPanel() },
                    { key: "stripe_webhooks", label: "Stripe webhooks", children: renderStripeWebhooksPanel() },
                ]}
            />
        </div>
    );

    return (
        <div className="admin-commercial-page">
            {contextHolder}
            <div className="admin-commercial-header">
                <div className="admin-commercial-header-main">
                    <div className="admin-commercial-kicker">
                        <SafetyCertificateOutlined /> SUPER_ADMIN uniquement
                    </div>
                    <div className="admin-commercial-header-title">
                        <Title level={2} style={{ margin: 0 }}>Commercial Control</Title>
                        <HelpTip
                            title="Comment utiliser cet écran"
                            items={[
                                "Plans : consulter le catalogue et éditer les champs admin autorisés.",
                                "Workspaces : attribuer / ajuster une licence client (droits produit uniquement).",
                                "Usage : diagnostiquer quotas, réservations et événements d'un workspace.",
                                "Adjustments : créer ou révoquer des bonus produit avec raison admin.",
                                "Policy messages : éditer les wording / sévérité des messages produit.",
                                "Observability : consulter audit events et webhooks Stripe redacted.",
                            ]}
                            placement="bottomLeft"
                        />
                    </div>
                    <Text type="secondary">
                        Administration produit V1 : plans, capabilities, subscriptions, diagnostics, adjustments, policy messages et observability support.
                    </Text>
                </div>
                <div className="admin-commercial-header-aside">
                    <Tag color="blue">Source de vérité backend</Tag>
                    <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                        Toute action exige une raison admin tracée.
                    </Text>
                </div>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                className="admin-commercial-tabs"
                items={[
                    { key: "plans", label: "Plans", children: renderPlansTab() },
                    { key: "subscriptions", label: "Workspaces / Subscriptions", children: renderSubscriptionTab() },
                    { key: "usage", label: "Usage diagnostics", children: renderUsageDiagnosticsTab() },
                    { key: "adjustments", label: "Adjustments", children: renderAdjustmentsTab() },
                    { key: "policy_messages", label: "Policy messages", children: renderPolicyMessagesTab() },
                    { key: "observability", label: "Observability", children: renderObservabilityTab() },
                ]}
            />

            <Drawer
                title={planDetail ? `Plan ${planDetail.code} v${planDetail.version}` : "Plan"}
                open={planDrawerOpen}
                onClose={() => setPlanDrawerOpen(false)}
                width={880}
            >
                {planDetail && (
                    <Descriptions bordered size="small" column={2} className="admin-commercial-descriptions">
                        <Descriptions.Item label="ID">{planDetail.id}</Descriptions.Item>
                        <Descriptions.Item label="Code">{planDetail.code}</Descriptions.Item>
                        <Descriptions.Item label="Version">{planDetail.version}</Descriptions.Item>
                        <Descriptions.Item label="Statut">{statusTag(planDetail.status)}</Descriptions.Item>
                        <Descriptions.Item label="Quota scope">{planDetail.quota_scope}</Descriptions.Item>
                        <Descriptions.Item label="Public">{planDetail.is_public ? "oui" : "non"}</Descriptions.Item>
                    </Descriptions>
                )}
                <Table
                    columns={capabilityColumns}
                    dataSource={capabilities}
                    loading={capabilitiesLoading}
                    rowKey="id"
                    size="small"
                    tableLayout="fixed"
                    pagination={{ pageSize: 8, showSizeChanger: false }}
                />
            </Drawer>

            <Modal
                title="Modifier le plan"
                open={planModalOpen}
                onCancel={() => setPlanModalOpen(false)}
                onOk={submitPlanPatch}
                okText="Enregistrer"
                okButtonProps={{ disabled: !canSubmitPlan }}
                destroyOnHidden
            >
                <Alert
                    type="warning"
                    showIcon
                    className="admin-commercial-alert"
                    message="Les champs code, version et id ne sont pas éditables depuis ce panneau."
                />
                <Form form={planForm} layout="vertical">
                    <Form.Item name="name" label="Nom" rules={[{ required: true, message: "Nom requis" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="status" label="Statut" rules={[{ required: true, message: "Statut requis" }]}>
                        <Select options={PLAN_STATUS_OPTIONS.map(value => ({ value, label: value }))} />
                    </Form.Item>
                    <Form.Item name="is_public" label="Public">
                        <Select options={[{ value: true, label: "oui" }, { value: false, label: "non" }]} />
                    </Form.Item>
                    <Form.Item name="sort_order" label="Ordre">
                        <InputNumber style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name="metadata_json" label="metadata_json">
                        <Input.TextArea rows={5} spellCheck={false} />
                    </Form.Item>
                    <Form.Item name="reason" label="Raison admin" rules={[{ required: true, message: "Raison obligatoire" }]}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Modifier capability ${editingCapability?.capability_key || ""}`}
                open={capabilityModalOpen}
                onCancel={() => setCapabilityModalOpen(false)}
                onOk={submitCapabilityPatch}
                okText="Enregistrer"
                okButtonProps={{ disabled: !canSubmitCapability }}
                destroyOnHidden
            >
                <Form form={capabilityForm} layout="vertical">
                    <Form.Item name="value_json" label="value_json" rules={[{ required: true, message: "value_json requis" }]}>
                        <Input.TextArea rows={9} spellCheck={false} />
                    </Form.Item>
                    <Form.Item name="reason" label="Raison admin" rules={[{ required: true, message: "Raison obligatoire" }]}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Libérer une réservation reserved"
                open={releaseModalOpen}
                onCancel={() => setReleaseModalOpen(false)}
                onOk={submitReleaseReservation}
                okText="Libérer"
                okButtonProps={{ danger: true, disabled: !canSubmitRelease }}
                destroyOnHidden
            >
                <Alert
                    type="warning"
                    showIcon
                    className="admin-commercial-alert"
                    message="Seules les réservations au statut reserved peuvent être libérées manuellement. Aucun usage confirmé n'est supprimé."
                />
                <Form form={releaseForm} layout="vertical">
                    <Form.Item name="reason" label="Raison admin" rules={[{ required: true, message: "Raison obligatoire" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="metadata_json" label="metadata_json">
                        <Input.TextArea rows={4} spellCheck={false} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Créer un adjustment"
                open={adjustmentModalOpen}
                onCancel={() => setAdjustmentModalOpen(false)}
                onOk={submitAdjustment}
                okText="Créer"
                okButtonProps={{ disabled: !canSubmitAdjustment }}
                destroyOnHidden
            >
                <Form form={adjustmentForm} layout="vertical">
                    <Form.Item name="usage_type" label="Usage type" rules={[{ required: true, message: "Usage requis" }]}>
                        <Select
                            options={USAGE_TYPE_ORDER.map(value => ({ value, label: USAGE_TYPE_LABELS[value] || value }))}
                            onChange={(value) => {
                                const options = adjustmentTypeOptionsForUsage(value);
                                adjustmentForm.setFieldsValue({
                                    adjustment_type: options.length === 1 ? options[0] : undefined,
                                });
                            }}
                        />
                    </Form.Item>
                    <Form.Item name="adjustment_type" label="Adjustment type" rules={[{ required: true, message: "Type requis" }]}>
                        <Select
                            options={adjustmentTypeOptionsForUsage(adjustmentValues.usage_type).map(value => ({
                                value,
                                label: ADJUSTMENT_TYPE_LABELS[value] || value,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="quantity" label="Quantité" rules={[{ required: true, message: "Quantité requise" }]}>
                        <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name="period_start" label="Début (period_start)" rules={[{ required: true, message: "Début requis" }]} tooltip="Sera envoyé au backend au format ISO 8601 UTC.">
                        <DatePicker
                            showTime
                            format="YYYY-MM-DD HH:mm"
                            style={{ width: "100%" }}
                            placeholder="Sélectionner un début"
                        />
                    </Form.Item>
                    <Form.Item name="period_end" label="Fin (period_end)" rules={[{ required: true, message: "Fin requise" }]} tooltip="Sera envoyé au backend au format ISO 8601 UTC.">
                        <DatePicker
                            showTime
                            format="YYYY-MM-DD HH:mm"
                            style={{ width: "100%" }}
                            placeholder="Sélectionner une fin"
                        />
                    </Form.Item>
                    <Form.Item name="metadata_json" label="metadata_json">
                        <Input.TextArea rows={4} spellCheck={false} />
                    </Form.Item>
                    <Form.Item name="reason" label="Raison admin" rules={[{ required: true, message: "Raison obligatoire" }]}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Révoquer un adjustment"
                open={revokeAdjustmentModalOpen}
                onCancel={() => setRevokeAdjustmentModalOpen(false)}
                onOk={submitRevokeAdjustment}
                okText="Révoquer"
                okButtonProps={{ danger: true, disabled: !canSubmitRevokeAdjustment }}
                destroyOnHidden
            >
                <Form form={revokeAdjustmentForm} layout="vertical">
                    <Form.Item name="reason" label="Raison admin" rules={[{ required: true, message: "Raison obligatoire" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="metadata_json" label="metadata_json">
                        <Input.TextArea rows={4} spellCheck={false} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Modifier policy message ${editingPolicyMessage?.code || ""}`}
                open={policyModalOpen}
                onCancel={() => setPolicyModalOpen(false)}
                onOk={submitPolicyPatch}
                okText="Enregistrer"
                okButtonProps={{ disabled: !canSubmitPolicy }}
                width={760}
                destroyOnHidden
            >
                <Form form={policyForm} layout="vertical" className="admin-commercial-form-grid">
                    <Form.Item name="title_key" label="title_key">
                        <Input />
                    </Form.Item>
                    <Form.Item name="body_key" label="body_key">
                        <Input />
                    </Form.Item>
                    <Form.Item name="severity" label="Sévérité">
                        <Select options={POLICY_SEVERITY_OPTIONS.map(value => ({ value, label: value }))} />
                    </Form.Item>
                    <Form.Item name="cta_target" label="cta_target">
                        <Input />
                    </Form.Item>
                    <Form.Item name="default_title_fr" label="Titre FR" className="admin-commercial-form-wide">
                        <Input />
                    </Form.Item>
                    <Form.Item name="default_body_fr" label="Corps FR" className="admin-commercial-form-wide">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="default_title_en" label="Titre EN" className="admin-commercial-form-wide">
                        <Input />
                    </Form.Item>
                    <Form.Item name="default_body_en" label="Corps EN" className="admin-commercial-form-wide">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="cta_label_key" label="cta_label_key">
                        <Input />
                    </Form.Item>
                    <Form.Item name="default_cta_label_fr" label="CTA FR">
                        <Input />
                    </Form.Item>
                    <Form.Item name="default_cta_label_en" label="CTA EN">
                        <Input />
                    </Form.Item>
                    <Form.Item name="metadata_json" label="metadata_json" className="admin-commercial-form-wide">
                        <Input.TextArea rows={4} spellCheck={false} />
                    </Form.Item>
                    <Form.Item name="reason" label="Raison admin" rules={[{ required: true, message: "Raison obligatoire" }]} className="admin-commercial-form-wide">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
